import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import useSWR from 'swr';
import { Contact, RoleType, ContactService } from '../services/contact.api.client';
import { useCompany } from './CompanyContext';
import { useConfig } from './ConfigContext';

interface LocalUpdate extends Contact {
  _updatedAt: number;
}

interface ContactContextType {
  contacts: Contact[];
  loading: boolean;
  loadings: Record<RoleType, boolean>;
  fetchContacts: (role: RoleType) => Promise<void>;
  createContact: (contact: Partial<Contact>) => Promise<any>;
  updateContact: (contactId: string, contact: Partial<Contact>) => Promise<any>;
}

const ContactContext = createContext<ContactContextType | undefined>(undefined);

const LOCAL_UPDATE_THRESHOLD = 15000; // 15 seconds

// SWR fetcher — key includes role, companyUid and configKey to scope the cache correctly
const contactFetcher = async ([, role, companyUid, configKey]: [string, RoleType, string, string]): Promise<Contact[]> => {
  const response = await ContactService.getBulkContacts(role, companyUid, configKey);
  if (response?.contactList) {
    return response.contactList.map((c: any) => ({ ...c, role })) as Contact[];
  }
  return [];
};

const SWR_OPTIONS = {
  // Do NOT auto-fetch on mount; only fetch when explicitly triggered via mutate / key change
  revalidateOnMount: false,
  // Revalidate when the user refocuses the browser tab (catches changes from other users)
  revalidateOnFocus: true,
  // Deduplicate identical requests within 60 seconds (prevents redundant calls on tab switches)
  dedupingInterval: 60_000,
};

export const ContactProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [localUpdates, setLocalUpdates] = useState<Record<string, LocalUpdate>>({});
  const { company } = useCompany();
  const { config } = useConfig();

  const companyUid = company?.current?.uid;
  const configKey = config.key;

  // SWR hook per role — key is null when company/config aren't ready yet
  const employeeKey = companyUid && configKey ? ['contacts', 'EMPLOYEE', companyUid, configKey] : null;
  const customerKey = companyUid && configKey ? ['contacts', 'CUSTOMER', companyUid, configKey] : null;
  const supplierKey = companyUid && configKey ? ['contacts', 'SUPPLIER', companyUid, configKey] : null;

  const { data: employeeData, isLoading: employeeLoading, mutate: mutateEmployees } =
    useSWR<Contact[]>(employeeKey, contactFetcher, SWR_OPTIONS);

  const { data: customerData, isLoading: customerLoading, mutate: mutateCustomers } =
    useSWR<Contact[]>(customerKey, contactFetcher, SWR_OPTIONS);

  const { data: supplierData, isLoading: supplierLoading, mutate: mutateSuppliers } =
    useSWR<Contact[]>(supplierKey, contactFetcher, SWR_OPTIONS);

  // When company changes, clear optimistic local updates (SWR cache is scoped by key automatically)
  React.useEffect(() => {
    if (companyUid) {
      setLocalUpdates({});
    }
  }, [companyUid]);

  const loading = employeeLoading || customerLoading || supplierLoading;

  // fetchContacts triggers a SWR revalidation (bypasses the dedupingInterval)
  const fetchContacts = useCallback(async (role: RoleType) => {
    if (role === 'EMPLOYEE') await mutateEmployees();
    else if (role === 'CUSTOMER') await mutateCustomers();
    else if (role === 'SUPPLIER') await mutateSuppliers();
  }, [mutateEmployees, mutateCustomers, mutateSuppliers]);

  const addLocalUpdate = useCallback((contact: Partial<Contact> & { uid: string }) => {
    setLocalUpdates(prev => {
      const existing = prev[contact.uid] || {};
      const role = contact.role || contact.roles?.[0]?.role_type || (existing as any).role;
      return {
        ...prev,
        [contact.uid]: {
          ...existing,
          ...contact,
          role,
          _updatedAt: Date.now()
        } as LocalUpdate
      };
    });
  }, []);

  const createContact = async (contact: Partial<Contact>) => {
    if (!companyUid || !configKey) return;
    const res = await ContactService.createContact(
      { ...contact, company_id: company.current.id },
      companyUid,
      configKey
    );
    if (res?.uid && !res.message && !res.err) {
      addLocalUpdate(res);
    }
    return res;
  };

  const updateContact = async (contactId: string, contact: Partial<Contact>) => {
    if (!companyUid || !configKey) return;
    const res = await ContactService.updateContact(
      contactId,
      { ...contact, company_id: company.current.id },
      companyUid,
      configKey
    );
    if (!res?.message && !res?.err) {
      const updateData = res?.uid ? res : { ...contact, uid: contactId };
      addLocalUpdate(updateData);
    }
    return res;
  };

  // Flatten all SWR data into a single array
  const serverContacts = useMemo<Contact[]>(() => [
    ...(employeeData || []),
    ...(customerData || []),
    ...(supplierData || []),
  ], [employeeData, customerData, supplierData]);

  // Merge server contacts with short-lived optimistic local updates
  const contacts = useMemo(() => {
    const now = Date.now();

    const activeLocalUpdates = Object.entries(localUpdates).reduce((acc, [uid, update]) => {
      if (now - update._updatedAt < LOCAL_UPDATE_THRESHOLD) {
        acc[uid] = update;
      }
      return acc;
    }, {} as Record<string, LocalUpdate>);

    if (Object.keys(activeLocalUpdates).length === 0) return serverContacts;

    const mergedMap = new Map<string, Contact>();

    serverContacts.forEach(c => {
      if (c.uid) {
        const local = activeLocalUpdates[c.uid];
        if (local) {
          const { _updatedAt, ...localContact } = local;
          mergedMap.set(c.uid, { ...c, ...localContact });
        } else {
          mergedMap.set(c.uid, c);
        }
      }
    });

    // Add newly created contacts not yet in server data
    Object.entries(activeLocalUpdates).forEach(([uid, local]) => {
      if (!mergedMap.has(uid)) {
        const { _updatedAt, ...localContact } = local;
        mergedMap.set(uid, localContact as Contact);
      }
    });

    return Array.from(mergedMap.values());
  }, [serverContacts, localUpdates]);

  const loadings = useMemo(() => ({
    EMPLOYEE: employeeLoading,
    CUSTOMER: customerLoading,
    SUPPLIER: supplierLoading
  }), [employeeLoading, customerLoading, supplierLoading]);

  return (
    <ContactContext.Provider value={{ contacts, loading, loadings, fetchContacts, createContact, updateContact }}>
      {children}
    </ContactContext.Provider>
  );
};

export const useContact = () => {
  const context = useContext(ContactContext);
  if (context === undefined) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
};
